import { Dialog, Transition } from "@headlessui/react";
// import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, ReactNode } from "react";
import Icon from "../Icon";

interface HeadlessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | ReactNode;
  description?: string | ReactNode;
  children: ReactNode;
  trigger?: ReactNode;
  onTriggerClick?: () => void;
  hideCloseButton?: boolean;
  size?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl"
    | "full";
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  position?: "center" | "top" | "bottom";
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  width?: string;
}

type DialogSize = NonNullable<HeadlessDialogProps["size"]>;

const sizeClasses: Record<DialogSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

const contentPaddingClasses: Record<DialogSize, string> = {
  sm: "p-4",
  md: "p-5",
  lg: "p-5 md:p-8",
  xl: "p-5 md:p-8",
  "2xl": "p-5 md:p-8",
  "3xl": "p-5 md:p-10",
  "4xl": "p-5 md:p-10",
  "5xl": "p-5 md:p-10",
  "6xl": "p-5 md:p-10",
  "7xl": "p-5 md:p-10",
  full: "p-0",
};

const positionClasses = {
  center: "items-center justify-center",
  top: "items-start justify-center pt-16",
  bottom: "items-end justify-center pb-16",
};

const HeadlessDialog = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  trigger,
  onTriggerClick,
  hideCloseButton = false,
  size = "md",
  className = "",
  overlayClassName = "",
  contentClassName = "",
  position = "center",
  closeOnOverlayClick = true,
  closeOnEscape = true,
  // width = "551px",
  width = "100%",
}: HeadlessDialogProps) => {
  return (
    <>
      {/* Trigger Button */}
      {trigger && (
        <button
          type="button"
          onClick={onTriggerClick}
          className={`inline-flex items-center justify-center rounded-md border border-transparent bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${className}`}
        >
          {trigger}
        </button>
      )}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[2147483646]"
          onClose={closeOnOverlayClick ? onClose : () => {}}
          static={!closeOnEscape}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={`fixed inset-0 transition-opacity ${overlayClassName}`}
              style={{
                zIndex: 2147483645,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              }}
            />
          </Transition.Child>

          <div
            className="fixed inset-0 overflow-y-auto"
            style={{ zIndex: 2147483646 }}
          >
            <div
              className={`flex min-h-full ${size === "full" ? "p-0" : "p-4 sm:p-0"} text-center ${positionClasses[position]}`}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`relative flex w-full transform flex-col overflow-hidden ${size === "full" ? "rounded-none" : "rounded-2xl ring-1 ring-gray-900/10"} bg-white text-left align-middle shadow-2xl transition-all ${sizeClasses[size]} ${size === "full" ? "my-0 h-screen max-h-screen w-screen" : "max-h-[90vh] sm:my-8"} sm:w-full ${contentClassName}`}
                  style={{
                    zIndex: 2147483647,
                    margin: "0 auto",
                    width: size === "full" ? "100vw" : width,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Close Button - Always positioned absolutely */}
                  {!hideCloseButton && (
                    <button
                      type="button"
                      className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-1 focus-visible:ring-offset-2"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <Icon iconName="close2" aria-hidden="true" />
                    </button>
                  )}

                  {/* Header */}
                  {(title || description) && (
                    <div
                      className={`relative z-10 flex shrink-0 items-start justify-between border-b border-gray-200/80 shadow-sm ${contentPaddingClasses[size]}`}
                      style={{ paddingBottom: "1.25rem" }}
                    >
                      <div
                        className={`min-w-0 flex-1 ${hideCloseButton ? "" : "pr-8"}`}
                      >
                        {title && (
                          <Dialog.Title
                            as="h3"
                            className="text-xl font-semibold leading-7 tracking-tight text-gray-900"
                          >
                            {title}
                          </Dialog.Title>
                        )}
                        {description && (
                          <Dialog.Description className="mt-1.5 text-sm leading-6 text-gray-500">
                            {description}
                          </Dialog.Description>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="min-h-0 overflow-y-auto">
                    <div
                      className={`flex items-center justify-center ${contentPaddingClasses[size]}`}
                      style={{
                        paddingTop: title || description ? "1.5rem" : undefined,
                      }}
                    >
                      {children}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default HeadlessDialog;
